import Auth0 from 'auth0-js'
import * as jwt from 'jsonwebtoken'
import config from '@/config'
import { Tenant } from '@/types'

/**
  Default maximum age (in seconds) of an Auth0 session accepted for the
  sensitive passkey enrolment flow (v2) — used when the deployment does not
  provide an explicit value. 15 minutes: tight enough to block sessions left
  unattended, generous enough for a user arriving straight from the product.
* */
export const DEFAULT_PASSKEY_ENROLLMENT_SESSION_MAX_AGE_SECONDS = 15 * 60

/**
  Hard ceiling for the configurable enrolment session max age. A window beyond
  this would let long-idle sessions through and defeat the gate's purpose, so
  out-of-range values (almost always a misconfiguration — e.g. days written
  as seconds) are clamped rather than honoured.
* */
export const MAX_PASSKEY_ENROLLMENT_SESSION_MAX_AGE_SECONDS = 60 * 60

/**
  Resolves the configured enrolment session max age from the raw app-config
  value: strict non-negative integer only, clamped to
  `[1, MAX_PASSKEY_ENROLLMENT_SESSION_MAX_AGE_SECONDS]`, falling back to the
  15-minute default for anything else (unset, empty, `"900abc"`, decimals).
* */
export const resolvePasskeyEnrollmentSessionMaxAgeSeconds = (raw: string | undefined): number => {

  if (typeof raw !== 'string' || !/^\d+$/.test(raw)) {
    return DEFAULT_PASSKEY_ENROLLMENT_SESSION_MAX_AGE_SECONDS
  }

  return Math.min(Math.max(1, parseInt(raw, 10)), MAX_PASSKEY_ENROLLMENT_SESSION_MAX_AGE_SECONDS)

}

/**
  Maximum age (in seconds) of an Auth0 session that is accepted for the
  sensitive passkey enrolment flow (v2). Configurable per deployment via the
  `PASSKEY_ENROLLMENT_SESSION_MAX_AGE_SECONDS` app config (`CONFIG_PASSKEY_ENROLLMENT_SESSION_MAX_AGE_SECONDS`
  in the FE `.env.<env>` files / deployed `static/config.js`); falls back to
  15 minutes when unset or invalid, and is clamped to the 1-hour hard ceiling.

  Enrolling a passkey creates a durable credential, so a *recently established*
  session is required — a session that is merely present (left unattended, or
  replayed from long ago) is treated as invalid so the page can send the user
  through a fresh login.
* */
export const PASSKEY_ENROLLMENT_SESSION_MAX_AGE_SECONDS = (
  resolvePasskeyEnrollmentSessionMaxAgeSeconds(config?.PASSKEY_ENROLLMENT_SESSION_MAX_AGE_SECONDS)
)

/**
  Check's the user's session against Auth0
* */
export const checkSession = (tenant: Tenant): Promise<boolean> => {

  return new Promise((resolve, reject) => {

    if (!tenant) {
      reject(new Error('TenantRequired'))
    }

    const session = {
      redirectUri: `${window.location.protocol}//${window.location.host}/authenticate`,
      responseType: 'token id_token',
    }

    const webAuth = new Auth0.WebAuth({
      domain: tenant.loginDomain,
      clientID: tenant.loginClientId,
    })

    webAuth.checkSession(session, (err) => {
      if (err) {
        reject(new Error('need to login'))
      } else {
        resolve(true)
      }
    })

  })
}

/**
  Silently mints a My Account API (`/me/`) access token for the logged-in user,
  reusing their existing Auth0 session.

  This is the user-plane token used to enrol a passkey via the Auth0 My Account
  API — NOT the admin-plane Management API token the beefs use. The tenant's
  login client must have the My Account API enabled with the
  `create:me:authentication_methods` scope (platform-auth0-config, PR #59 in dev).
* */
export const getUserToken = (tenant: Tenant): Promise<string> => {

  return new Promise((resolve, reject) => {

    if (!tenant) {
      reject(new Error('TenantRequired'))
      return
    }

    const session = {
      audience: `https://${tenant.loginDomain}/me/`,
      scope: 'openid profile create:me:authentication_methods',
      responseType: 'token',
      redirectUri: `${window.location.protocol}//${window.location.host}/authenticate`,
    }

    const webAuth = new Auth0.WebAuth({
      domain: tenant.loginDomain,
      clientID: tenant.loginClientId,
    })

    webAuth.checkSession(session, (err, authResult) => {
      if (err || !authResult?.accessToken) {
        reject(new Error('need to login'))
      } else {
        resolve(authResult.accessToken)
      }
    })

  })
}

/**
  Key of the tenant's session-introspection claim (added by the
  enrich-profile-with-geoip Auth0 action on all SSE tenants — verified in
  platform-auth0-config-{dev,preprod,pciprod}). It carries `authenticated_at`,
  the moment the user authenticated.
* */
export const SESSION_CLAIM_KEY = 'https://siteminder.com/session'

/**
  Resolves the user's authentication time (epoch seconds) from an id_token
  payload. Prefers the standard `auth_time` claim; falls back to the tenant
  session claim's `authenticated_at` — SSO logins do not always mint
  `auth_time` (verified on the dev tenant 2026-09-29: the silent-flow
  id_token had no `auth_time`, but the session claim carried
  `authenticated_at`).

  Resolved values are sanity-bounded to a plausible epoch-seconds window
  (see AUTH_TIME_MIN_EPOCH_SECONDS / AUTH_TIME_MAX_FUTURE_SKEW_SECONDS).
  The freshness gate is one-sided — it only rejects *old* sessions — so a
  future-dated or garbage auth time (clock skew, ms-scaled value, malformed
  claim) would otherwise yield a negative age and always look "fresh".

  Returns `null` when no signal is present, when a signal is unparseable,
or when a present signal falls outside the plausible window — callers must
  treat that as an invalid session (fail closed).
* */

/**
  Lower bound for a plausible authentication time (epoch seconds).
  1_000_000_000 is 2001-09-09 — nothing legitimate is older, and values
  below it indicate a missing/zeroed/garbage claim.
*/
export const AUTH_TIME_MIN_EPOCH_SECONDS = 1_000_000_000

/**
  How far into the future an authentication time may be before it is treated
  as untrusted — tolerates small browser ↔ Auth0 clock skew, rejects
  future-dated and millisecond-scaled values (e.g. Date.now()).
*/
export const AUTH_TIME_MAX_FUTURE_SKEW_SECONDS = 5 * 60

const isPlausibleAuthTime = (epochSeconds: number, nowSeconds: number): boolean => {
  return epochSeconds >= AUTH_TIME_MIN_EPOCH_SECONDS
    && epochSeconds <= nowSeconds + AUTH_TIME_MAX_FUTURE_SKEW_SECONDS
}

export const resolveSessionAuthTime = (payload: any): number | null => {

  const nowSeconds = Math.floor(Date.now() / 1000)

  const rawAuthTime = payload?.auth_time

  if (rawAuthTime !== null && rawAuthTime !== undefined) {
    // Present but untrusted (implausible, non-numeric) → fail closed
    // rather than falling through to the session claim.
    const standard = Number(rawAuthTime)
    if (Number.isFinite(standard) && isPlausibleAuthTime(standard, nowSeconds)) {
      return standard
    }
    return null
  }

  const authenticatedAt = payload?.[SESSION_CLAIM_KEY]?.authenticated_at

  if (typeof authenticatedAt === 'string' && authenticatedAt) {
    const ms = Date.parse(authenticatedAt)
    if (!Number.isNaN(ms)) {
      const epochSeconds = Math.floor(ms / 1000)
      if (isPlausibleAuthTime(epochSeconds, nowSeconds)) {
        return epochSeconds
      }
    }
  }

  return null

}

/**
  Checks the user's Auth0 session for the sensitive passkey enrolment flow (v2).

  Unlike `checkSession` (which only asserts that a session exists), this also
  enforces a maximum session age via the id_token's `auth_time` claim (or the
  tenant session claim's `authenticated_at` — see `resolveSessionAuthTime`).
  Enrolling a passkey creates a durable credential, so we only accept a
  recently established session; a session that is merely present (e.g. left
  unattended or replayed from long ago) resolves `false` so the page can
  redirect the user through a fresh login.

  Fails closed: a missing id_token, or an id_token with no resolvable
  authentication time, resolves `false` rather than throwing.
* */
export const checkEnrollmentSession = (tenant: Tenant): Promise<boolean> => {

  return new Promise((resolve, reject) => {

    if (!tenant) {
      reject(new Error('TenantRequired'))
      return
    }

    const session = {
      redirectUri: `${window.location.protocol}//${window.location.host}/authenticate`,
      responseType: 'token id_token',
    }

    const webAuth = new Auth0.WebAuth({
      domain: tenant.loginDomain,
      clientID: tenant.loginClientId,
    })

    webAuth.checkSession(session, (err, authResult) => {

      if (err || !authResult?.idToken) {
        resolve(false)
        return
      }

      const payload = jwt.decode(authResult.idToken) as any
      const authTime = resolveSessionAuthTime(payload)
      const ageSeconds = authTime === null ? null : Math.floor(Date.now() / 1000) - authTime
      const verdict = authTime === null || ageSeconds > PASSKEY_ENROLLMENT_SESSION_MAX_AGE_SECONDS

      if (verdict) {
        resolve(false)
        return
      }

      resolve(true)

    })

  })
}
